import { type CSSProperties, memo, type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import Imgix, { type SharedImgixAndSourceProps } from 'react-imgix';

import { isValidUrl } from '@air/utilities';

import { isImgixUrl } from './isImgixUrl';

export type ImgixImageProps = {
  /** The alt text for the image - this should be passed in for accessibility purposes */
  alt: string;
  /** This is the backup URL of the image in the event that Imgix is down */
  fallbackUrl?: string;
  /** Use this callback to fire when either the Imgix or fallback img loads successfully */
  onLoad?: (e?: SyntheticEvent<HTMLImageElement>) => void;
  /** Use to disable drag which is true by default- needed for image annotations */
  draggable?: boolean;
  style?: CSSProperties;
} & SharedImgixAndSourceProps;

const DEFAULT_IMGIX_PARAMS: SharedImgixAndSourceProps['imgixParams'] = { auto: 'format' };

/**
 * This component is a wrapper around the react-imgix component and it's purpose is to allow a fallback url (`fallbackUrl`) to load in the event that the Imgix service is down. You'd be surprised how often this happens.
 * All props are spread onto the react-imgix component with the exception of fallbackUrl and onError.
 */
export const ImgixImage = memo(
  ({ fallbackUrl = '', onLoad, alt, draggable = true, style, imgixParams, ...restOfProps }: ImgixImageProps) => {
    const [error, setError] = useState(false);

    const isUrl = useMemo(() => isValidUrl(restOfProps.src), [restOfProps.src]);
    const isImgix = useMemo(() => isImgixUrl(restOfProps.src), [restOfProps.src]);

    const htmlAttributes = useMemo(
      () => ({
        alt,
        draggable,
        onLoad,
        style,
      }),
      [alt, style, draggable, onLoad],
    );

    const memoizedImgixParams = useMemo(
      () => (typeof imgixParams === 'undefined' ? DEFAULT_IMGIX_PARAMS : { ...DEFAULT_IMGIX_PARAMS, ...imgixParams }),
      [imgixParams],
    );

    const handleError = useCallback((err: unknown) => {
      setError(!!err);
    }, []);

    if (!isUrl) {
      return null;
    }

    if (!isImgix) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`ImgixImage: src "${restOfProps.src}" is not an imgix URL. imgix transformations will not apply.`);
      }

      return (
        // biome-ignore lint/performance/noImgElement: non-imgix URL, render as plain img
        <img
          {...htmlAttributes}
          alt={alt}
          className={restOfProps.className}
          height={restOfProps.height}
          sizes={restOfProps.sizes}
          src={restOfProps.src}
          width={restOfProps.width}
        />
      );
    }

    if (error) {
      // biome-ignore lint/performance/noImgElement: fallback for when imgix is unavailable
      // biome-ignore lint/a11y/useAltText: alt is included in htmlAttributes
      return <img {...htmlAttributes} src={fallbackUrl} />;
    }

    return (
      <Imgix
        {...restOfProps}
        htmlAttributes={{
          ...htmlAttributes,
          onError: handleError,
        }}
        imgixParams={memoizedImgixParams}
      />
    );
  },
);

ImgixImage.displayName = 'ImgixImage';
